import { useState } from 'react';
import { Box, MenuItem, Menu } from '@mui/material';
import { WorkspacesIcon, MemoriesIcon, LeftChevron } from 'components/SVG';
import Dropzone, { Accept, FileRejection } from 'react-dropzone';
import { MAX_FILE_SIZE } from 'constants/index';
import { UploadIcon } from 'lucide-react';
import type { Workspace } from 'types/Workspace';
import type { Memory } from 'types/Memory';
import WorkspacesPopover from './WorkspacesPopover';
import KnowledgePopover from './KnowledgePopover';
import { useInfiniteMemoriesQuery } from 'hooks/memory';
import React from 'react';
import { useDebouncedState } from 'hooks/common';
import { useInfiniteWorkspaceSearch } from 'hooks/Workspace/useInfiniteWorkspaceSearch';

const acceptedFileTypes: Accept = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  'text/plain': ['.txt'],
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/svg+xml': ['.svg'],
};

type AddIconMenuProps = {
  onFileUpload: (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => Promise<(string | undefined)[] | undefined>;
  disabled: boolean;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onAddWorkspaceAttachment: (workspace: Workspace) => void;
  onAddKnowledgeAttachment: (knowledge: Memory) => void;
};

type MenuView = 'main' | 'workspaces' | 'knowledge';

const AddIconMenu = ({
  onFileUpload,
  disabled,
  open,
  anchorEl,
  onClose,
  onAddWorkspaceAttachment,
  onAddKnowledgeAttachment,
}: AddIconMenuProps) => {
  const [menuView, setMenuView] = useState<MenuView>('main');

  const [query, setQuery] = React.useState('');
  const debouncedQuery = useDebouncedState(query);
  const {
    joinedMemories,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteMemoriesQuery({}, !query);

  const {
    workspaces,
    isLoading: isSearchingForWorkspaces,
    fetchNextPage: fetchNextWorkspacePage,
    hasNextPage: hasNextWorkspacePage,
    isFetchingNextPage: isFetchingNextPageWorkspace,
  } = useInfiniteWorkspaceSearch({ query: debouncedQuery });

  const handleClose = () => {
    onClose();
    setMenuView('main');
  };

  const handleBack = () => setMenuView('main');

  const handleLatElementView = React.useCallback(() => {
    if (!hasNextPage || isFetchingNextPage || !!query) {
      return;
    }

    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, query]);

  const WorkspaceHandleLatElementView = React.useCallback(() => {
    if (!hasNextWorkspacePage || isFetchingNextPageWorkspace || !!query) {
      return;
    }

    fetchNextWorkspacePage();
  }, [
    fetchNextWorkspacePage,
    hasNextWorkspacePage,
    isFetchingNextPageWorkspace,
    query,
  ]);

  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      elevation={2}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      sx={{
        mt: '-5px',
        '& .MuiPaper-root': {
          borderRadius: '8px',
          width: 'auto',
          maxWidth: 'auto',
        },
      }}
    >
      {menuView === 'main' && (
        <>
          <MenuItem onClick={() => setMenuView('workspaces')}>
            <Box
              display='flex'
              justifyContent='space-between'
              gap={2}
              width='100%'
            >
              <Box display='flex' alignItems='center' gap={1}>
                <WorkspacesIcon color='black' />
                Workspaces
              </Box>
              <LeftChevron />
            </Box>
          </MenuItem>

          <MenuItem onClick={() => setMenuView('knowledge')}>
            <Box
              display='flex'
              justifyContent='space-between'
              gap={2}
              width='100%'
            >
              <Box display='flex' alignItems='center' gap={1}>
                <MemoriesIcon stroke='black' />
                Knowledge Sources
              </Box>
              <LeftChevron />
            </Box>
          </MenuItem>

          <Dropzone
            disabled={disabled}
            onDrop={onFileUpload}
            multiple
            accept={acceptedFileTypes}
            maxSize={MAX_FILE_SIZE}
          >
            {({ getRootProps, getInputProps }) => (
              <MenuItem {...getRootProps()}>
                <Box display='flex' alignItems='center' gap={1}>
                  <UploadIcon color='black' />
                  Upload image or file
                </Box>
                <input {...getInputProps()} />
              </MenuItem>
            )}
          </Dropzone>
        </>
      )}

      {menuView === 'workspaces' && (
        <WorkspacesPopover
          onBack={handleBack}
          onAddWorkspaceAttachment={(WorkSpace) => {
            onAddWorkspaceAttachment(WorkSpace);
            handleBack();
          }}
          WorkspaceHandleLatElementView={WorkspaceHandleLatElementView}
          isSearchingForWorkspaces={isSearchingForWorkspaces}
          data={workspaces}
          isFetchingNextPage={isFetchingNextPageWorkspace}
          hasNextPage={hasNextWorkspacePage}
        />
      )}

      {menuView === 'knowledge' && (
        <KnowledgePopover
          onBack={handleBack}
          onAddKnowledgeAttachment={(MemoryPopover) => {
            onAddKnowledgeAttachment(MemoryPopover);
            handleBack();
          }}
          onLastElementView={handleLatElementView}
          isLoading={isLoading}
          joinedMemories={joinedMemories}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
        />
      )}
    </Menu>
  );
};

export default AddIconMenu;
